import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SdMembership {
  isMember: boolean;
  group?: string;
  membershipId?: string;
  validFrom?: string;
  validTo?: string;
}

export interface SdSubsidy {
  code: string;
  name: string;
  baseAmount: number;
  coverage: number;
  maxPerYear?: number;
  conditions?: string;
  group?: string;
  validFrom?: string;
  validTo?: string;
  grantLinesPerTreatment?: number;
}

export interface SdSubsidiesResult {
  membership: SdMembership;
  subsidies: SdSubsidy[];
}

export interface SdSubmitResult {
  success: boolean;
  uuid: string | null;
  code: number | null;
  message: string | null;
  errors: Array<{ field?: string; message?: string }>;
}

export interface SdValidateResult {
  valid: boolean;
  code: number | null;
  message: string | null;
  errors: Array<{ field?: string; message?: string }>;
}

export interface SdStatusResult {
  uuid: string;
  status: unknown;
}

export type SdClaimStatus =
  | 'pending'
  | 'processing'
  | 'approved'
  | 'rejected'
  | 'paid'
  | 'cancelled';

export interface SdClaim {
  id: string;
  clinic_id: string;
  invoice_id: string;
  patient_id: string;
  sd_uuid: string;
  sd_invoice_number: string;
  status: SdClaimStatus;
  submitted_at: string;
  submitted_by: string | null;
  last_checked_at: string | null;
  last_status_update: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  rejection_reason: string | null;
  rejection_code: string | null;
  amount_cents: number;
  approved_amount_cents: number | null;
  treatment_count: number;
  api_response: unknown;
  status_history: Array<{
    status: SdClaimStatus;
    timestamp: string;
    user_id?: string;
    notes?: string;
  }>;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CheckClaimStatusResult {
  checked_count: number;
  updated_count: number;
  results: Array<{
    sd_uuid: string;
    old_status: string;
    new_status: string | null;
    updated: boolean;
    error?: string;
  }>;
}

export interface BatchSubmitResult {
  total: number;
  succeeded: number;
  failed: number;
  results: Array<{
    invoice_id: string;
    invoice_number: string | null;
    success: boolean;
    sd_uuid: string | null;
    error?: string;
  }>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const sdSubsidiesQueryKey = (
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) => ['sd', 'subsidies', clinicId, patientId] as const;

export const sdClaimQueryKey = (claimId: string | null | undefined) =>
  ['sd', 'claim', claimId] as const;

export const sdClaimsQueryKey = (
  clinicId: string | null | undefined,
  patientId?: string | null | undefined,
) => ['sd', 'claims', clinicId, patientId] as const;

// ---------------------------------------------------------------------------
// Helper: call Edge Function
// ---------------------------------------------------------------------------

async function callSdFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<{
    ok: boolean;
    data?: T;
    error?: { code: string; message: string };
  }>(name, {
    body,
  });
  if (error) throw new Error(error.message ?? 'Edge Function error');
  if (!data?.ok) throw new Error(data?.error?.message ?? `${name} failed`);
  return data.data as T;
}

// ---------------------------------------------------------------------------
// useGetSubsidies — imperative mutation (caller holds the CPR)
// ---------------------------------------------------------------------------

export function useGetSubsidies() {
  return useMutation({
    mutationFn: ({ clinic_id, cpr }: { clinic_id: string; cpr: string }) =>
      callSdFunction<SdSubsidiesResult>('sygeforsikring-get-subsidies', { clinic_id, cpr }),
  });
}

// ---------------------------------------------------------------------------
// useValidateInvoice
// ---------------------------------------------------------------------------

export function useValidateInvoice() {
  return useMutation({
    mutationFn: ({ clinic_id, invoice_id }: { clinic_id: string; invoice_id: string }) =>
      callSdFunction<SdValidateResult>('sygeforsikring-validate-invoice', {
        clinic_id,
        invoice_id,
      }),
  });
}

// ---------------------------------------------------------------------------
// useSubmitInvoice
// ---------------------------------------------------------------------------

export function useSubmitInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clinic_id, invoice_id }: { clinic_id: string; invoice_id: string }) =>
      callSdFunction<SdSubmitResult>('sygeforsikring-submit-invoice', { clinic_id, invoice_id }),
    onSuccess: (_data, { invoice_id }) => {
      // Invalidate invoice detail so the UI picks up the new sd_uuid
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', invoice_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// useCheckStatus
// ---------------------------------------------------------------------------

export function useCheckStatus() {
  return useMutation({
    mutationFn: ({ clinic_id, uuid }: { clinic_id: string; uuid: string }) =>
      callSdFunction<SdStatusResult>('sygeforsikring-check-status', { clinic_id, uuid }),
  });
}

// ---------------------------------------------------------------------------
// useCreditInvoice
// ---------------------------------------------------------------------------

export function useCreditInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clinic_id,
      original_invoice_id,
      credit_invoice_id,
    }: {
      clinic_id: string;
      original_invoice_id: string;
      credit_invoice_id: string;
    }) =>
      callSdFunction<SdSubmitResult>('sygeforsikring-credit-invoice', {
        clinic_id,
        original_invoice_id,
        credit_invoice_id,
      }),
    onSuccess: (_data, { credit_invoice_id }) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', credit_invoice_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// useClinicSdEnabled — reads sygeforsikring_enabled from the clinic row
// ---------------------------------------------------------------------------

export function useClinicSdEnabled(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['clinics', 'sd-enabled', clinicId],
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<{ enabled: boolean; providerId: string | null }> => {
      if (!clinicId) return { enabled: false, providerId: null };
      const { data, error } = await supabase
        .from('clinics')
        .select('sygeforsikring_enabled, sygeforsikring_provider_id')
        .eq('id', clinicId)
        .single();
      if (error || !data) return { enabled: false, providerId: null };
      return {
        enabled: Boolean((data as Record<string, unknown>).sygeforsikring_enabled),
        providerId:
          ((data as Record<string, unknown>).sygeforsikring_provider_id as string) ?? null,
      };
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// useUpdateSdSettings — update clinic SD config (platform admin only)
// ---------------------------------------------------------------------------

export function useUpdateSdSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clinic_id,
      sygeforsikring_enabled,
      sygeforsikring_provider_id,
    }: {
      clinic_id: string;
      sygeforsikring_enabled: boolean;
      sygeforsikring_provider_id: string | null;
    }) => {
      // Use update_clinic_settings RPC which goes through the audit trail
      const { error } = await supabase.rpc('update_clinic_settings', {
        p_clinic_id: clinic_id,
        p_payload: {
          sygeforsikring_enabled,
          sygeforsikring_provider_id: sygeforsikring_provider_id ?? null,
        },
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { clinic_id }) => {
      qc.invalidateQueries({ queryKey: ['clinics', 'sd-enabled', clinic_id] });
      qc.invalidateQueries({ queryKey: ['clinics', clinic_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Claim Summary Type (matches RPC return shape)
// ---------------------------------------------------------------------------

export interface SdClaimSummary {
  claim_id: string;
  invoice_id: string;
  invoice_number: string;
  sd_uuid: string;
  status: SdClaimStatus;
  submitted_at: string;
  amount_cents: number;
  approved_amount_cents: number | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
}

// ---------------------------------------------------------------------------
// Claim Management Hooks (CLINIO-122)
// ---------------------------------------------------------------------------

/**
 * Get claims for a patient in a clinic via RPC.
 * Note: Both clinicId and patientId are required as the underlying RPC
 * `get_patient_sygeforsikring_claims` requires both parameters.
 */
export function useSdClaims(
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: sdClaimsQueryKey(clinicId, patientId),
    enabled: Boolean(clinicId && patientId),
    queryFn: async (): Promise<SdClaimSummary[]> => {
      if (!clinicId || !patientId) return [];

      const { data, error } = await supabase.rpc('get_patient_sygeforsikring_claims', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
      });

      if (error) throw new Error(error.message);

      return (data ?? []).map((row) => ({
        claim_id: row.claim_id,
        invoice_id: row.invoice_id,
        invoice_number: row.invoice_number,
        sd_uuid: row.sd_uuid,
        status: row.status as SdClaimStatus,
        submitted_at: row.submitted_at,
        amount_cents: row.amount_cents,
        approved_amount_cents: row.approved_amount_cents ?? null,
        approved_at: row.approved_at ?? null,
        rejected_at: row.rejected_at ?? null,
        rejection_reason: row.rejection_reason ?? null,
      }));
    },
    staleTime: 30_000,
  });
}

/**
 * Get pending claims (for admin status checking).
 * Uses `get_pending_sygeforsikring_claims` RPC.
 */
export function usePendingSdClaims(options?: { limit?: number; maxAgeHours?: number }) {
  return useQuery({
    queryKey: ['sd', 'pending-claims', options?.limit, options?.maxAgeHours],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_pending_sygeforsikring_claims', {
        p_limit: options?.limit,
        p_max_age_hours: options?.maxAgeHours,
      });

      if (error) throw new Error(error.message);
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

/**
 * Get single claim from patient claims.
 * Since there's no direct "get by claim ID" RPC, this fetches patient claims
 * and filters to find the requested claim.
 */
export function useSdClaim(
  claimId: string | null | undefined,
  clinicId: string | null | undefined,
  patientId: string | null | undefined,
) {
  return useQuery({
    queryKey: sdClaimQueryKey(claimId),
    enabled: Boolean(claimId && clinicId && patientId),
    queryFn: async (): Promise<SdClaimSummary | null> => {
      if (!claimId || !clinicId || !patientId) return null;

      const { data, error } = await supabase.rpc('get_patient_sygeforsikring_claims', {
        p_clinic_id: clinicId,
        p_patient_id: patientId,
      });

      if (error) throw new Error(error.message);

      const claim = (data ?? []).find((row) => row.claim_id === claimId);
      if (!claim) return null;

      return {
        claim_id: claim.claim_id,
        invoice_id: claim.invoice_id,
        invoice_number: claim.invoice_number,
        sd_uuid: claim.sd_uuid,
        status: claim.status as SdClaimStatus,
        submitted_at: claim.submitted_at,
        amount_cents: claim.amount_cents,
        approved_amount_cents: claim.approved_amount_cents ?? null,
        approved_at: claim.approved_at ?? null,
        rejected_at: claim.rejected_at ?? null,
        rejection_reason: claim.rejection_reason ?? null,
      };
    },
    staleTime: 30_000,
  });
}

/**
 * Check claim status for a specific claim or all pending claims for a clinic
 */
export function useCheckClaimStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sd_uuid,
      clinic_id,
    }: {
      sd_uuid?: string;
      clinic_id?: string;
    }) =>
      callSdFunction<CheckClaimStatusResult>('sygeforsikring-check-claim-status', {
        sd_uuid,
        clinic_id,
      }),
    onSuccess: (_data, { clinic_id }) => {
      // Invalidate all claims queries for this clinic
      qc.invalidateQueries({ queryKey: ['sd', 'claims', clinic_id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

/**
 * Batch submit multiple invoices to Sygeforsikring Danmark
 */
export function useBatchSubmitInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clinic_id,
      invoice_ids,
    }: {
      clinic_id: string;
      invoice_ids: string[];
    }) =>
      callSdFunction<BatchSubmitResult>('sygeforsikring-batch-submit', {
        clinic_id,
        invoice_ids,
      }),
    onSuccess: (_data, { clinic_id }) => {
      // Invalidate invoice and claims queries
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['sd', 'claims', clinic_id] });
    },
  });
}

/**
 * Get patient claims history via RPC
 */
export function usePatientClaimsHistory(
  patientId: string | null | undefined,
  clinicId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['sd', 'patient-claims', clinicId, patientId],
    enabled: Boolean(clinicId && patientId),
    queryFn: async () => {
      if (!clinicId || !patientId) return [];

      const { data, error } = await supabase.rpc('get_patient_sygeforsikring_claims', {
        p_patient_id: patientId,
        p_clinic_id: clinicId,
      });

      if (error) throw new Error(error.message);
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

